import { KameleoonStorageKey } from 'src/storage';
import { TargetingType } from 'src/targeting';
import { ERROR_MESSAGES } from './constants';
import { exhaustCheck } from './helpers';
import { KameleoonException } from './types';

/**
 * @class
 * KameleoonError - extends standard `Error` by adding custom `type` property with the type of `KameleoonException`
 * */
export class KameleoonError extends Error {
  private errorType: KameleoonException;

  /**
   * @param {KameleoonException} type - type of KameleoonError represented by `KameleoonException` enum
   * */
  constructor(
    type:
      | KameleoonException.Initialization
      | KameleoonException.Credentials
      | KameleoonException.VisitorCodeMaxLength
      | KameleoonException.VisitorCodeEmpty
      | KameleoonException.VisitAmount
      | KameleoonException.StorageInitialization
      | KameleoonException.EventSourceInitialization
      | KameleoonException.StorageEmpty,
  );
  constructor(
    type:
      | KameleoonException.FeatureFlagConfigurationNotFound
      | KameleoonException.FeatureFlagEnvironmentDisabled,
    featureKey: string,
  );
  constructor(
    type:
      | KameleoonException.FeatureFlagConfigurationNotFound
      | KameleoonException.FeatureFlagEnvironmentDisabled,
    featureKey: string,
    environment: string,
  );
  constructor(
    type: KameleoonException.FeatureFlagVariableNotFound,
    variableKey: string,
    visitorCode: string,
  );
  constructor(
    type: KameleoonException.FeatureFlagVariationNotFound,
    variationKey: string,
    visitorCode: string,
  );
  constructor(
    type: KameleoonException.FeatureFlagExperimentNotFound,
    experimentId: number,
    visitorCode: string,
  );
  constructor(type: KameleoonException.CookieParse, message: string);
  constructor(
    type:
      | KameleoonException.StorageWrite
      | KameleoonException.JSONParse
      | KameleoonException.RemoteData
      | KameleoonException.MaximumRetriesReached
      | KameleoonException.ClientConfiguration,
    err: unknown,
  );
  constructor(type: KameleoonException.StorageRead, storageKey: string);
  constructor(
    type: KameleoonException.StorageParse,
    err: unknown,
    key: KameleoonStorageKey,
  );
  constructor(
    type: KameleoonException.TargetingCondition,
    targetingType: TargetingType,
  );
  constructor(
    type: KameleoonException.AmongValuesCheck,
    err: unknown,
    value: string,
  );
  constructor(type: KameleoonException.RangeCheck, value?: number[] | null);
  constructor(
    type:
      | KameleoonException.NumberParse
      | KameleoonException.VersionParse
      | KameleoonException.SemanticVersionParse,
    value: string,
  );
  constructor(
    type: KameleoonException,
    secondParam?:
      | number
      | string
      | unknown
      | KameleoonStorageKey
      | TargetingType,
    thirdParam?: string | KameleoonStorageKey,
  ) {
    super(`Error: ${type}`);

    this.name = 'KameleoonError';
    this.errorType = type;

    switch (type) {
      case KameleoonException.Initialization:
      case KameleoonException.Credentials:
      case KameleoonException.VisitorCodeMaxLength:
      case KameleoonException.VisitorCodeEmpty:
      case KameleoonException.StorageInitialization:
      case KameleoonException.VisitAmount:
      case KameleoonException.EventSourceInitialization:
      case KameleoonException.StorageEmpty:
        this.message = ERROR_MESSAGES[type]();
        break;
      case KameleoonException.CookieParse:
      case KameleoonException.FeatureFlagConfigurationNotFound:
      case KameleoonException.NumberParse:
      case KameleoonException.ClientConfiguration:
      case KameleoonException.MaximumRetriesReached:
      case KameleoonException.RemoteData:
      case KameleoonException.VersionParse:
      case KameleoonException.SemanticVersionParse:
        this.message = ERROR_MESSAGES[type](secondParam as string);
        break;
      case KameleoonException.FeatureFlagExperimentNotFound:
        this.message = ERROR_MESSAGES[type](
          secondParam as number,
          thirdParam as string,
        );
        break;
      case KameleoonException.FeatureFlagVariationNotFound:
      case KameleoonException.FeatureFlagVariableNotFound:
        this.message = ERROR_MESSAGES[type](
          secondParam as string,
          thirdParam as string,
        );
        break;
      case KameleoonException.FeatureFlagEnvironmentDisabled:
        if (thirdParam !== undefined) {
          this.message = ERROR_MESSAGES[type](
            secondParam as string,
            thirdParam as string,
          );
        } else {
          this.message = secondParam as string;
        }
        break;
      case KameleoonException.StorageWrite:
      case KameleoonException.JSONParse:
        this.message = ERROR_MESSAGES[type](secondParam);
        break;
      case KameleoonException.StorageRead:
        this.message = ERROR_MESSAGES[type](secondParam as KameleoonStorageKey);
        break;
      case KameleoonException.StorageParse:
        this.message = ERROR_MESSAGES[type](
          secondParam,
          thirdParam as KameleoonStorageKey,
        );
        break;
      case KameleoonException.TargetingCondition:
        this.message = ERROR_MESSAGES[type](secondParam as TargetingType);
        break;
      case KameleoonException.AmongValuesCheck:
        this.message = ERROR_MESSAGES[type](secondParam, thirdParam as string);
        break;
      case KameleoonException.RangeCheck:
        this.message = ERROR_MESSAGES[type](
          secondParam as number[] | null | undefined,
        );
        break;
      default:
        exhaustCheck(type);
    }
  }

  get type(): KameleoonException {
    return this.errorType;
  }
}
